import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import { mapAdminSeries, mapCreation, mapEpisode, mapReelClip, mapSeries, mapVoteResponse } from './mappers';
import type { AdminSeriesItem, CreationSubmission, EpisodeItem, ReelClip, SeriesItem, VoteResponse } from '../types/content';

export async function fetchReels(): Promise<ReelClip[]> {
  const snapshot = await getDocs(
    query(collection(db, 'studio_episodes'), where('status', '==', 'published'), orderBy('publishedAt', 'desc'), limit(40)),
  );
  const docs = snapshot.docs;
  const [socialById, seriesTitleById] = await Promise.all([fetchSocialStats(docs), fetchSeriesTitles(docs)]);
  return docs.map((entry) => mapReelClip(entry, socialById[entry.id], seriesTitleById)).filter((clip): clip is ReelClip => clip !== null);
}

async function fetchSocialStats(docs: QueryDocumentSnapshot<DocumentData>[]): Promise<Record<string, DocumentData>> {
  const snapshots = await Promise.all(docs.map((entry) => getDoc(doc(db, 'reel_social', entry.id)).catch(() => null)));
  const socialById: Record<string, DocumentData> = {};
  snapshots.forEach((entry) => {
    if (entry?.exists()) socialById[entry.id] = entry.data();
  });
  return socialById;
}

async function fetchSeriesTitles(docs: QueryDocumentSnapshot<DocumentData>[]): Promise<Record<string, string>> {
  const ids = Array.from(
    new Set(
      docs
        .map((entry) => String(entry.data().seriesId || '').trim())
        .filter((id) => id.length > 0),
    ),
  );
  const snapshots = await Promise.all(ids.map((id) => getDoc(doc(db, 'series', id)).catch(() => null)));
  const titlesById: Record<string, string> = {};
  snapshots.forEach((entry) => {
    if (entry?.exists()) titlesById[entry.id] = String(entry.data().title || '');
  });
  return titlesById;
}

export async function fetchSeries(): Promise<SeriesItem[]> {
  const snapshot = await getDocs(
    query(collection(db, 'series'), where('status', '==', 'published'), orderBy('createdAt', 'desc'), limit(50)),
  );
  return snapshot.docs.map(mapSeries);
}

export async function fetchSeriesDetail(seriesId: string): Promise<{ series: SeriesItem | null; episodes: EpisodeItem[] }> {
  const [seriesSnapshot, episodeSnapshot] = await Promise.all([
    getDoc(doc(db, 'series', seriesId)),
    getDocs(
      query(
        collection(db, 'episodes'),
        where('seriesId', '==', seriesId),
        orderBy('seasonNumber', 'asc'),
        orderBy('episodeNumber', 'asc'),
      ),
    ),
  ]);

  return {
    series: seriesSnapshot.exists() ? mapSeries(seriesSnapshot as QueryDocumentSnapshot<DocumentData>) : null,
    episodes: episodeSnapshot.docs.map(mapEpisode),
  };
}

export async function submitCreation(input: Omit<CreationSubmission, 'id' | 'status' | 'adminNote' | 'createdAt' | 'updatedAt'>) {
  const ref = doc(collection(db, 'website_creations'));
  await setDoc(ref, {
    ...input,
    status: 'new',
    adminNote: '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function submitVoteResponse(input: Omit<VoteResponse, 'id' | 'createdAt' | 'updatedAt'>) {
  await setDoc(
    doc(db, 'website_vote_responses', input.uid),
    {
      ...input,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export async function fetchAdminData(): Promise<{ creations: CreationSubmission[]; votes: VoteResponse[]; series: AdminSeriesItem[] }> {
  const [creationsSnapshot, votesSnapshot, series] = await Promise.all([
    getDocs(query(collection(db, 'website_creations'), orderBy('createdAt', 'desc'), limit(100))),
    getDocs(query(collection(db, 'website_vote_responses'), orderBy('updatedAt', 'desc'), limit(100))),
    fetchAdminSeries(),
  ]);

  return {
    creations: creationsSnapshot.docs.map(mapCreation),
    votes: votesSnapshot.docs.map(mapVoteResponse),
    series,
  };
}

async function fetchAdminSeries(): Promise<AdminSeriesItem[]> {
  const snapshot = await getDocs(query(collection(db, 'series'), limit(200)));
  const counts = await Promise.all(
    snapshot.docs.map((entry) =>
      getCountFromServer(query(collection(db, 'episodes'), where('seriesId', '==', entry.id)))
        .then((countSnapshot) => countSnapshot.data().count)
        .catch(() => 0),
    ),
  );
  return snapshot.docs
    .map((entry, index) => mapAdminSeries(entry, counts[index] ?? 0))
    .sort((a, b) => {
      if (a.status === 'published' && b.status !== 'published') return -1;
      if (a.status !== 'published' && b.status === 'published') return 1;
      return a.title.localeCompare(b.title);
    });
}

export async function updateCreationReview(id: string, status: CreationSubmission['status'], adminNote: string) {
  await updateDoc(doc(db, 'website_creations', id), {
    status,
    adminNote,
    updatedAt: serverTimestamp(),
  });
}

export async function updateSeriesVisibility(id: string, visible: boolean) {
  await updateDoc(doc(db, 'series', id), {
    status: visible ? 'published' : 'hidden',
    updatedAt: serverTimestamp(),
  });
}
